import { useRef, useState } from "react";
import { Calendar, ChevronRight, GripVertical, MessageSquare, Plus } from "lucide-react";
import { Spinner } from "@/components/ui/Spinner";
import { UserAvatar } from "@/components/ui/UserAvatar";
import {
  useCreateSubTask,
  useReorderSubTasks,
  useSubTasks,
} from "@/features/tasks/hooks/useSubTasks";
import {
  TASK_STATUS_COLORS,
  TASK_STATUS_LABELS,
  type SubTask,
} from "@/features/tasks/types";
import { SubTaskDetailPanel } from "@/features/tasks/components/SubTaskDetailPanel";

interface Props {
  taskId: string;
}

function weekChip(weeks: number | null): { text: string; cls: string } | null {
  if (weeks === null) return null;
  const text = weeks === 0 ? "0 tyg." : weeks > 0 ? `+${weeks} tyg.` : `${weeks} tyg.`;
  const cls =
    weeks < -1 ? "text-red-700 font-semibold"
    : weeks === -1 ? "text-red-500 font-medium"
    : weeks === 0 ? "text-orange-600 font-semibold"
    : weeks === 1 ? "text-yellow-600"
    : "text-gray-400 dark:text-gray-500";
  return { text, cls };
}

type OverSlot = { id: string; side: "top" | "bottom" } | null;

interface SubTaskRowProps {
  subtask: SubTask;
  allSubtasks: SubTask[];
  taskId: string;
  depth: number;
  onOpen: (id: string) => void;
  // drag-drop only used at depth 0
  dragHandlers?: {
    onDragStart: (id: string) => void;
    onDragOver: (e: React.DragEvent, id: string) => void;
    onDrop: (e: React.DragEvent, id: string) => void;
    onDragEnd: () => void;
  };
  overSlot: OverSlot;
}

function SubTaskRow({
  subtask, allSubtasks, taskId, depth, onOpen,
  dragHandlers, overSlot,
}: SubTaskRowProps) {
  const children = allSubtasks
    .filter((s) => s.parent === subtask.id)
    .sort((a, b) => a.position - b.position || a.created_at.localeCompare(b.created_at));

  const [expanded, setExpanded] = useState(false);
  const isRoot = depth === 0;
  const due = weekChip(subtask.weeks_until_due);

  const isLineTop = overSlot?.id === subtask.id && overSlot.side === "top";
  const isLineBottom = overSlot?.id === subtask.id && overSlot.side === "bottom";

  return (
    <li className="select-none">
      {/* Insert-before line indicator */}
      {isLineTop && (
        <div className="h-0.5 bg-blue-500 rounded-full mx-2 mb-0.5" />
      )}

      <div
        draggable={isRoot}
        onDragStart={isRoot && dragHandlers ? () => dragHandlers.onDragStart(subtask.id) : undefined}
        onDragOver={isRoot && dragHandlers ? (e) => dragHandlers.onDragOver(e, subtask.id) : undefined}
        onDrop={isRoot && dragHandlers ? (e) => dragHandlers.onDrop(e, subtask.id) : undefined}
        onDragEnd={isRoot && dragHandlers ? dragHandlers.onDragEnd : undefined}
        onClick={() => onOpen(subtask.id)}
        className="group flex items-center gap-2 rounded-lg px-2 py-1.5 cursor-pointer border border-transparent hover:bg-gray-50 dark:hover:bg-gray-700/50 hover:border-gray-200 transition-colors"
        style={{ paddingLeft: `${8 + depth * 18}px` }}
      >
        {/* Expand/collapse toggle */}
        <span
          className="flex-shrink-0 w-[14px] flex items-center justify-center"
          onClick={(e) => {
            if (children.length > 0) { e.stopPropagation(); setExpanded((v) => !v); }
          }}
        >
          {children.length > 0 ? (
            <ChevronRight
              size={12}
              className={`text-gray-400 dark:text-gray-500 transition-transform ${expanded ? "rotate-90" : ""}`}
            />
          ) : null}
        </span>

        {/* Drag handle (root only) */}
        {isRoot ? (
          <span
            className="text-gray-300 dark:text-gray-600 hover:text-gray-500 dark:hover:text-gray-400 flex-shrink-0 cursor-grab active:cursor-grabbing"
            onClick={(e) => e.stopPropagation()}
          >
            <GripVertical size={13} />
          </span>
        ) : (
          <span className="flex-shrink-0 w-[13px]" />
        )}

        {/* Status badge */}
        <span
          className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 font-medium ${TASK_STATUS_COLORS[subtask.status]}`}
        >
          {TASK_STATUS_LABELS[subtask.status]}
        </span>

        {/* Cascade indicator */}
        {subtask.done_by_cascade && (
          <span
            title="Ukończone automatycznie przez ukończenie podzadania nadrzędnego"
            className="text-xs px-1.5 py-0.5 rounded-full flex-shrink-0 bg-green-50 text-green-500 border border-green-200 dark:bg-green-950 dark:text-green-400 dark:border-green-800 cursor-default"
          >
            ↑ kaskada
          </span>
        )}

        {/* Title */}
        <span
          className={`text-sm flex-1 truncate ${
            ["done", "cancelled", "n_d"].includes(subtask.status)
              ? "line-through text-gray-400 dark:text-gray-500"
              : "text-gray-800 dark:text-gray-200"
          }`}
        >
          {subtask.title}
        </span>

        {/* Children count badge (when collapsed) */}
        {children.length > 0 && !expanded && (
          <span className="text-xs text-gray-400 dark:text-gray-500 flex-shrink-0 bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded-full">
            {children.length}
          </span>
        )}

        {/* Assignee avatars */}
        {subtask.assignees.length > 0 && (
          <div className="flex -space-x-1 flex-shrink-0">
            {subtask.assignees.slice(0, 3).map((a) => (
              <UserAvatar
                key={a.id}
                userId={a.id}
                fullName={a.full_name}
                profilePictureUrl={a.profile_picture_url}
                size="sm"
                border
              />
            ))}
            {subtask.assignees.length > 3 && (
              <div className="w-5 h-5 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400 text-xs flex items-center justify-center border border-white">
                +{subtask.assignees.length - 3}
              </div>
            )}
          </div>
        )}

        {/* Due week */}
        {due && (
          <div className="flex items-center gap-0.5 flex-shrink-0">
            <Calendar size={11} className="text-gray-400 dark:text-gray-500" />
            <span className={`text-xs ${due.cls}`}>{due.text}</span>
          </div>
        )}

        {/* Comment count */}
        {subtask.comments_count > 0 && (
          <div className="flex items-center gap-0.5 text-xs text-gray-400 dark:text-gray-500 flex-shrink-0">
            <MessageSquare size={11} />
            <span>{subtask.comments_count}</span>
          </div>
        )}
      </div>

      {/* Insert-after line indicator */}
      {isLineBottom && (
        <div className="h-0.5 bg-blue-500 rounded-full mx-2 mt-0.5" />
      )}

      {/* Children */}
      {expanded && children.length > 0 && (
        <ul className="border-l border-gray-200 dark:border-gray-700 ml-5">
          {children.map((child) => (
            <SubTaskRow
              key={child.id}
              subtask={child}
              allSubtasks={allSubtasks}
              taskId={taskId}
              depth={depth + 1}
              onOpen={onOpen}
              overSlot={overSlot}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

// ─── Main export ───────────────────────────────────────────────────────────

export function SubTaskList({ taskId }: Props) {
  const { data: subtasks = [], isLoading } = useSubTasks(taskId);
  const { mutate: createSubTask, isPending: creating } = useCreateSubTask(taskId);
  const { mutate: reorder } = useReorderSubTasks();

  const [newTitle, setNewTitle] = useState("");
  const [openSubtaskId, setOpenSubtaskId] = useState<string | null>(null);

  // Drag state (root level only)
  const dragId = useRef<string | null>(null);
  const [overSlot, setOverSlot] = useState<OverSlot>(null);

  const rootSubtasks = subtasks
    .filter((s) => !s.parent)
    .sort((a, b) => a.position - b.position || a.created_at.localeCompare(b.created_at));

  const done = subtasks.filter((s) => s.status === "done").length;
  const total = subtasks.length;

  function handleAdd() {
    const title = newTitle.trim();
    if (!title) return;
    createSubTask({ title }, { onSuccess: () => setNewTitle("") });
  }

  const dragHandlers = {
    onDragStart(id: string) {
      dragId.current = id;
    },
    onDragOver(e: React.DragEvent, id: string) {
      e.preventDefault();
      if (dragId.current === id) return;
      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
      const pct = (e.clientY - rect.top) / rect.height;
      setOverSlot({ id, side: pct < 0.5 ? "top" : "bottom" });
    },
    onDrop(e: React.DragEvent, targetId: string) {
      e.preventDefault();
      const sourceId = dragId.current;
      if (!sourceId || sourceId === targetId) {
        dragId.current = null;
        setOverSlot(null);
        return;
      }

      // Calculate side at drop time for accuracy
      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
      const pct = (e.clientY - rect.top) / rect.height;
      const side = pct < 0.5 ? "top" : "bottom";

      const ids = rootSubtasks.map((s) => s.id);
      const fromIdx = ids.indexOf(sourceId);
      if (fromIdx === -1) { dragId.current = null; setOverSlot(null); return; }

      const reordered = [...ids];
      reordered.splice(fromIdx, 1);
      const newToIdx = reordered.indexOf(targetId);
      if (newToIdx === -1) { dragId.current = null; setOverSlot(null); return; }

      const insertIdx = side === "bottom" ? newToIdx + 1 : newToIdx;
      reordered.splice(insertIdx, 0, sourceId);

      reorder({ taskId, ids: reordered });
      dragId.current = null;
      setOverSlot(null);
    },
    onDragEnd() {
      dragId.current = null;
      setOverSlot(null);
    },
  };

  if (isLoading) return <div className="flex justify-center py-4"><Spinner size="sm" /></div>;

  return (
    <div>
      {/* Progress bar */}
      {total > 0 && (
        <div className="mb-3">
          <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mb-1">
            <span>Podzadania</span>
            <span>{done}/{total} ukończone</span>
          </div>
          <div className="h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
            <div
              className="h-full bg-[var(--color-primary)] dark:bg-[var(--color-primary)] rounded-full transition-all"
              style={{ width: `${total > 0 ? (done / total) * 100 : 0}%` }}
            />
          </div>
        </div>
      )}

      {/* Subtask rows */}
      <ul className="space-y-0.5 mb-3">
        {rootSubtasks.map((st) => (
          <SubTaskRow
            key={st.id}
            subtask={st}
            allSubtasks={subtasks}
            taskId={taskId}
            depth={0}
            onOpen={setOpenSubtaskId}
            dragHandlers={dragHandlers}
            overSlot={overSlot}
          />
        ))}
      </ul>

      {/* Add new root subtask */}
      <div className="flex items-center gap-2">
        <Plus size={14} className="text-gray-400 dark:text-gray-500 flex-shrink-0" />
        <input
          value={newTitle}
          onChange={(e) => setNewTitle(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleAdd()}
          placeholder="Dodaj podzadanie..."
          disabled={creating}
          className="flex-1 text-sm text-gray-700 dark:text-gray-300 placeholder-gray-400 bg-transparent outline-none border-b border-transparent focus:border-[var(--color-primary)] py-0.5"
        />
        {newTitle.trim() && (
          <button
            onClick={handleAdd}
            disabled={creating}
            className="text-xs text-[var(--color-primary)] hover:text-[var(--color-primary)] font-medium"
          >
            Dodaj
          </button>
        )}
      </div>

      {/* Subtask detail panel */}
      {openSubtaskId && (
        <SubTaskDetailPanel
          taskId={taskId}
          subtaskId={openSubtaskId}
          onClose={() => setOpenSubtaskId(null)}
        />
      )}
    </div>
  );
}
